import {gql} from '@apollo/client';
import classNames from 'classnames';
import React from 'react';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import * as NavConstants from 'constants/NavConstants';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {Pagination} from 'lib/Listings';
import {LoadingState} from 'models/reduceLoadingState';
import {RootCrumb} from 'nav/Breadcrumbs';
import ListingHeader from 'nav/sidepanel/header/ListingHeader';
import SidepanelListingSettingsDropdown from 'nav/sidepanel/header/SidepanelListingSettingsDropdown';
import BenchlingAppSidepanelItem from 'nav/sidepanel/listing/BenchlingAppSidepanelItem';
import CompactListingBanner from 'nav/sidepanel/listing/CompactListingBanner';
import CompactResultsPagerWrapper from 'nav/sidepanel/listing/CompactResultsPagerWrapper';
import ListingToolbarArea from 'nav/sidepanel/listing/ListingToolbarArea';
import {getNavigationURL} from 'nav/sidepanel/listing/SearchURLs';
import SelectedRowsCounter from 'nav/sidepanel/listing/SelectedRowsCounter';
import {getSelectedPageSize} from 'nav/sidepanel/PageSizeUtil';
import ToggleDropdown from 'nav/ToggleDropdown';
import notifications from 'notifications';
import BenchlingProfiler from 'performance/BenchlingProfiler';
import {composeSidepanelSearchResultsRenderedEvent} from 'performance/processSearchEvents';
import ClearSearchButton from 'search/ClearSearchButton';
import QueryInput from 'search/QueryInput';
import {
  Filter,
  FilterKey,
  keyToEmptyFilter,
  LISTING_ERROR_MESSAGE,
  SearchParams,
  SearchType,
  Sort,
  SortKey,
} from 'search/SearchConstants';
import * as SearchPreferences from 'search/SearchPreferences';
import * as SearchUtil from 'search/SearchUtil';
import withSearching, {NavigationUpdate, SearchProps} from 'search/withSearching';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import * as nyc from 'ui-library/NYC';
import ResultsPager from 'ui-library/ResultsPager';
import {cycleColumnHeaderSort} from 'ui-library/scrollable/ScrollableTableUtil';

import {
  BenchlingAppSidepanelQuery,
  BenchlingAppSidepanelQueryVariables,
} from './__generated/BenchlingAppSidepanelQuery';
import BenchlingAppsExpandedTable from './BenchlingAppExpandedTable';

type BenchlingAppSidepanelQueryBenchlingApp = BenchlingAppSidepanelQuery['benchlingApps'][number];

const BENCHLING_APP_SORT_KEYS = [SortKey.SORT_CREATED_AT_KEY, SortKey.SORT_NAME_KEY];

export function defaultBenchlingAppsSearchParams(): SearchParams {
  return {
    filters: [{...keyToEmptyFilter(FilterKey.TYPES), value: [ModelTypeConstants.BENCHLING_APP]}],
    limit: getSelectedPageSize(),
    offset: 0,
    query: '',
    sorts: [{key: SortKey.SORT_CREATED_AT_KEY, reverse: false}],
  };
}

export const BENCHLING_APP_SIDEPANEL_LISTING_QUERY = gql`
  query BenchlingAppSidepanelQuery($ids: [StrictString!]!) @skipAudit @engComponent(name: BENCHLING_APPS) {
    benchlingApps(ids: $ids) {
      id
      name
      lastSeen
    }
  }
`;

interface BenchlingAppsSidepanelProps {
  isExpanded: boolean;
  isCompactDisplay: boolean;
  setIsCompactDisplay: (isCompact: boolean) => void;
  activeItemId: string | null;
  connectionsTab: JSX.Element;
}

type Props = BenchlingAppsSidepanelProps & SearchProps;

interface BenchlingAppsSidepanelInternalProps extends Props {
  handleSearch: (searchParams: SearchParams) => void;
  handleSetFilters: (filters: Array<Filter>) => void;
  handleSetQuery: (query: string) => void;
  handleSetPageSize: (limit: number) => void;
  handleSetSortOrder: (sorts: Array<Sort>) => void;
  getPagination: () => Pagination;
  handleSetOffset: (offset: number) => void;
  isClearEnabled: () => boolean;
  handleClear: () => void;
}

interface BenchlingAppsSidepanelContentsProps extends BenchlingAppsSidepanelInternalProps {
  // benchlingApps will always be the empty array ([]) when isLoading is true.
  benchlingApps: Array<BenchlingAppSidepanelQueryBenchlingApp>;
  isLoading: boolean;
  getEmptyMessage: () => string;
}

function BenchlingAppsResultsPager(props: BenchlingAppsSidepanelInternalProps): JSX.Element {
  const pagination = props.getPagination();

  return (
    <ResultsPager
      isLoading={!props.searchResults}
      pagination={pagination}
      setOffset={props.handleSetOffset}
    />
  );
}

function BenchlingAppsSidepanelHeader(props: BenchlingAppsSidepanelInternalProps): JSX.Element {
  return (
    <ListingHeader
      buttons={<ToggleDropdown className="u-mini-spacing-right" expanded={props.isExpanded} />}
      crumbs={<RootCrumb text="Connections" />}
      isExpanded={props.isExpanded}
      queryInput={<QueryInput onUpdateQuery={props.handleSetQuery} query={props.searchParams.query} />}
      filtersContainer={props.isClearEnabled() ? <ClearSearchButton onClear={props.handleClear} /> : <div />}
      sectionTabs={props.connectionsTab}
    />
  );
}

export function CollapsedSidepanelContents(props: BenchlingAppsSidepanelContentsProps): JSX.Element {
  const pagination = props.getPagination();

  return (
    <>
      <nyc.Row>
        <BenchlingAppsSidepanelHeader {...props} />
      </nyc.Row>
      <nyc.Row>
        {props.isClearEnabled() && (
          <CompactListingBanner
            total={props.searchResults ? props.searchResults.total : null}
            isExact={props.searchResults ? props.searchResults.isExact : null}
          />
        )}
      </nyc.Row>
      <nyc.Row
        aria-labelledby={`${NavConstants.Section.BENCHLING_APPS}_TAB`}
        className="u-vertical-flex"
        id={`${NavConstants.Section.BENCHLING_APPS}_PANEL`}
        fluid={true}
        role="tabpanel"
      >
        <div className="u-flex-grow u-position-relative">
          <div className="u-absolute-scroll-container">
            {(() => {
              if (props.isLoading) {
                return <CenteredHorizontalThrobber />;
              } else {
                const apps = props.benchlingApps;
                return (
                  <>
                    <CompactResultsPagerWrapper pagination={pagination} setOffset={props.handleSetOffset}>
                      <BenchlingProfiler {...composeSidepanelSearchResultsRenderedEvent()}>
                        {apps.length === 0 ? (
                          <div className="sidepanel-fadedMessage">{props.getEmptyMessage()}</div>
                        ) : (
                          <>
                            {apps.map((app) => (
                              <BenchlingAppSidepanelItem
                                isActive={app.id === props.activeItemId}
                                key={app.id}
                                benchlingApp={app}
                                isCompact={props.isCompactDisplay}
                                onOpen={() => {
                                  FileBrowserActions.openFile(app.id);
                                }}
                                isChild={false}
                              />
                            ))}
                          </>
                        )}
                      </BenchlingProfiler>
                    </CompactResultsPagerWrapper>
                  </>
                );
              }
            })()}
          </div>
        </div>
      </nyc.Row>
    </>
  );
}

export function ExpandedSidepanelContents(props: BenchlingAppsSidepanelContentsProps): JSX.Element {
  const handleSetSortsByKey = (sortKey: string): void => {
    const newSort = cycleColumnHeaderSort(props.searchParams.sorts, sortKey);
    props.onUpdateSortOrder([newSort]);
  };

  return (
    <nyc.Row fluid={true}>
      <nyc.Col lg={1} />
      <nyc.Col lg={20} className="u-flex-display u-vertical-flex">
        <BenchlingAppsSidepanelHeader {...props} />
        <div className="u-lr-split">
          <div className="u-lr-split">
            <div className="u-flex-display">
              <BenchlingAppsResultsPager {...props} />
            </div>
            <SidepanelListingSettingsDropdown
              onSetSortOrder={props.handleSetSortOrder}
              onSetPageSize={props.handleSetPageSize}
              searchParams={props.searchParams}
              availableSortKeys={BENCHLING_APP_SORT_KEYS}
              displaySettingsConfig={{
                isCompact: props.isCompactDisplay,
                setIsCompactDisplay: props.setIsCompactDisplay,
              }}
            />
          </div>
          <ListingToolbarArea>
            <SelectedRowsCounter numSelected={props.selectedIds.length} />
          </ListingToolbarArea>
        </div>
        <div
          aria-labelledby={NavConstants.Section.BENCHLING_APPS}
          className="u-flex-grow u-flex-display u-small-spacing-top"
          role="tabpanel"
        >
          <BenchlingProfiler {...composeSidepanelSearchResultsRenderedEvent()}>
            <BenchlingAppsExpandedTable
              isCompact={props.isCompactDisplay}
              emptyMessage={props.getEmptyMessage()}
              isLoading={props.isLoading}
              benchlingApps={props.benchlingApps}
              sorts={props.searchParams.sorts}
              onChangeSort={handleSetSortsByKey}
              onUpdateSelectedIds={props.onSelectIds}
              selectedIds={props.selectedIds}
            />
          </BenchlingProfiler>
        </div>
      </nyc.Col>
      <nyc.Col lg={1} />
    </nyc.Row>
  );
}

export function BenchlingAppsSidepanelContents(props: Props): JSX.Element {
  const handleSearch = (searchParams: SearchParams): void => {
    props.onSearch(searchParams);
    SearchPreferences.rememberAppliedSortsToUserPreference(
      NavConstants.Section.BENCHLING_APPS,
      searchParams.sorts
    );
  };

  const handleClear = (): void => {
    handleSearch(
      SearchUtil.clearSearchParams(props.searchParams, {
        preservedFilterKeys: [],
        filtersToReset: defaultBenchlingAppsSearchParams().filters,
      })
    );
  };

  const isClearEnabled = (): boolean =>
    SearchUtil.hasQueryOrNonDefaultFilters(props.searchParams, [], [ModelTypeConstants.BENCHLING_APP]);

  const getPagination = (): Pagination => ({
    offset: props.searchParams.offset,
    limit: props.searchParams.limit,
    total: props.searchResults?.total || 0,
    isExact: props.searchResults?.isExact || true,
  });

  const actionFunctionProps = {
    handleSearch,
    handleSetFilters: props.onUpdateFilters,
    handleSetQuery: props.onUpdateQueryString,
    handleSetPageSize: props.onUpdatePageSize,
    handleSetSortOrder: props.onUpdateSortOrder,
    handleClear,
    isClearEnabled,
    getPagination,
    handleSetOffset: props.onUpdateOffset,
  };
  const queryResult = useSkippableBenchlingQuery<
    BenchlingAppSidepanelQuery,
    BenchlingAppSidepanelQueryVariables
  >(BENCHLING_APP_SIDEPANEL_LISTING_QUERY, {
    skip: !props.searchResults,
    variables: {ids: props.searchResults ? props.searchResults.ids : []},
  });

  const isLoading =
    !props.searchResults ||
    queryResult.type === QueryResultType.PENDING ||
    queryResult.type === QueryResultType.SKIPPED;
  const isErrored = queryResult.type === QueryResultType.ERROR;
  let benchlingApps: Array<BenchlingAppSidepanelQueryBenchlingApp> = [];
  if (isErrored) {
    notifications.error('Could not load apps; try reloading the page.');
  } else if (!isLoading) {
    benchlingApps = queryResult.data.benchlingApps;
  }

  const getEmptyMessage = (): string =>
    props.loadingState === LoadingState.FAILED ? LISTING_ERROR_MESSAGE : 'No apps';

  return props.isExpanded ? (
    <ExpandedSidepanelContents
      {...props}
      {...actionFunctionProps}
      isLoading={isLoading}
      benchlingApps={benchlingApps}
      getEmptyMessage={getEmptyMessage}
    />
  ) : (
    <CollapsedSidepanelContents
      {...props}
      {...actionFunctionProps}
      isLoading={isLoading}
      benchlingApps={benchlingApps}
      getEmptyMessage={getEmptyMessage}
    />
  );
}

export function BenchlingAppsSidepanel(props: Props): JSX.Element {
  return (
    <nyc.Container
      className={classNames('sidepanel', {expandedContainer: props.isExpanded})}
      size={props.isExpanded ? 'lg' : 'sidepanel'}
    >
      <BenchlingAppsSidepanelContents {...props} />
    </nyc.Container>
  );
}

export function getBenchlingAppsURL(prevProps: Props, props: Props): NavigationUpdate | null {
  return props.isExpanded
    ? {
        url: getNavigationURL('/apps', props.searchParams, props.filterErrors),
        options: {replace: prevProps.isExpanded === props.isExpanded},
      }
    : null;
}

export default withSearching(
  BenchlingAppsSidepanel,
  SearchType.BENCHLING_APPS,
  NavConstants.Section.BENCHLING_APPS,
  {defaultSearchParams: defaultBenchlingAppsSearchParams(), getNavigationUpdate: getBenchlingAppsURL}
);
